/**
 * Player progress store — keyed by level *identity*, not level *position*.
 *
 * ## Why
 *
 * v1 stored a single bare integer under `pxflow-progress`: the highest 1-based
 * level NUMBER the player had ever cleared. A level number is nothing but an
 * index into `LEVEL_METADATA` plus one, so every re-import, cull, hide or
 * ladder reorder silently re-pointed that number at a different level. The
 * cohort waves do all four, repeatedly.
 *
 * v2 stores the stable identity of every cleared level under
 * `pxflow-progress-v2`, so reordering the ladder moves a level's number but
 * never its progress.
 *
 * ## Identity key
 *
 * `uuid` when the level has one (1,702 of 1,704 do), else `file:<filename>`.
 * The generated filename is unique and just as reorder-proof, so the two
 * uuid-less legacy levels are not second-class citizens.
 *
 * ## The v1 key is never deleted
 *
 * It is kept as a backup and rewritten as a best-effort mirror on every save:
 * a rollback to a pre-migration build keeps working, and if v2 is ever cleared
 * or corrupted the mirror is what migration recovers from.
 *
 * ## Failure policy
 *
 * `localStorage` can throw on mere property access (cookies blocked), on
 * `getItem` and on `setItem` (Safari private mode, quota). Every access here
 * is wrapped: reads degrade to "no progress", writes degrade to `false`.
 * Nothing in this module ever throws at a caller.
 */

import { LEVEL_METADATA } from '../levels/index';

/** Legacy key: a bare decimal integer, the highest cleared level number. */
export const PROGRESS_KEY_V1 = 'pxflow-progress';
/** Current key: JSON `ProgressData`. */
export const PROGRESS_KEY_V2 = 'pxflow-progress-v2';

export const PROGRESS_VERSION = 2;

/** The slice of `LevelMeta` the store needs — keeps this module testable. */
export interface LevelIdentity {
  uuid?: string;
  file: string;
}

export interface ProgressData {
  version: number;
  /** Identity keys of every cleared level, in the order they were recorded. */
  completed: string[];
  /**
   * The raw v1 integer this record was migrated from, when it was. Provenance
   * only — the keys it produced are an *inferred* prefix, not observed wins
   * (see `migrateFromV1`).
   */
  migratedFrom?: number;
}

/** Stable identity key for a level's metadata entry. */
export function levelKey(meta: LevelIdentity | undefined): string | undefined {
  if (!meta) return undefined;
  return meta.uuid ?? `file:${meta.file}`;
}

function emptyProgress(): ProgressData {
  return { version: PROGRESS_VERSION, completed: [] };
}

/** `localStorage`, or null when it is unavailable or blocked. */
function storage(): Storage | null {
  try {
    return (globalThis as { localStorage?: Storage }).localStorage ?? null;
  } catch {
    return null;
  }
}

function readRaw(key: string): string | null {
  const s = storage();
  if (!s) return null;
  try {
    return s.getItem(key);
  } catch {
    return null;
  }
}

function writeRaw(key: string, value: string): boolean {
  const s = storage();
  if (!s) return false;
  try {
    s.setItem(key, value);
    return true;
  } catch {
    // Quota exceeded, or private-mode storage that accepts nothing.
    return false;
  }
}

/** Parse a stored v2 record. Returns null for anything unrecognizable. */
function parseV2(raw: string | null): ProgressData | null {
  if (!raw) return null;
  let obj: unknown;
  try {
    obj = JSON.parse(raw);
  } catch {
    return null;
  }
  if (!obj || typeof obj !== 'object') return null;
  const rec = obj as { completed?: unknown; migratedFrom?: unknown };
  if (!Array.isArray(rec.completed)) return null;

  const seen = new Set<string>();
  const completed: string[] = [];
  for (const k of rec.completed) {
    if (typeof k !== 'string' || k.length === 0 || seen.has(k)) continue;
    seen.add(k);
    completed.push(k);
  }
  const data: ProgressData = { version: PROGRESS_VERSION, completed };
  if (typeof rec.migratedFrom === 'number' && Number.isFinite(rec.migratedFrom)) {
    data.migratedFrom = rec.migratedFrom;
  }
  return data;
}

/**
 * Translate a v1 value into a v2 record.
 *
 * What the old integer actually referenced: `GameOverScene` wrote
 * `String(data.level)` on a win, and only when strictly greater than the
 * stored value. `data.level` is the 1-based level number that
 * `GameSceneLaunchData` converts to a `LEVEL_METADATA` index with `level - 1`.
 * So the stored value N means "the furthest position the player has cleared,
 * in the ladder order that was live when it was written" — which is today's
 * `LEVEL_METADATA` order, since nothing has reordered the ladder since the
 * value was last written.
 *
 * We mark positions 1..N cleared. The game gates nothing, so a player could
 * have jumped straight to N from the picker and never touched 1..N-1; the
 * prefix is therefore an over-claim in that case. It is still the right call:
 * every consumer of v1 (the CONTINUE label, the level-select page) read the
 * value as "you're up to here", and marking the prefix preserves that reading
 * *by identity* — after a reorder those same N levels stay cleared, whereas
 * marking only level N would collapse all progress onto wherever that one
 * level landed.
 *
 * Returns null only when there is no v1 value at all.
 */
export function migrateFromV1(
  rawV1: string | null,
  levels: readonly LevelIdentity[] = LEVEL_METADATA,
): ProgressData | null {
  if (rawV1 === null) return null;

  const data = emptyProgress();
  // parseInt, exactly as the v1 readers did — same coercion, same NaN.
  const highest = parseInt(rawV1, 10);
  if (!Number.isFinite(highest) || highest <= 0) return data;

  // Clamp: levels may have been culled since the value was written.
  const count = Math.min(Math.floor(highest), levels.length);
  for (let i = 0; i < count; i++) {
    const key = levelKey(levels[i]);
    if (key) data.completed.push(key);
  }
  data.migratedFrom = highest;
  return data;
}

/**
 * Current progress. Migrates from v1 on first call after the upgrade, and
 * again if v2 is ever lost or corrupted (the v1 backup is never deleted).
 */
export function loadProgress(levels: readonly LevelIdentity[] = LEVEL_METADATA): ProgressData {
  const v2 = parseV2(readRaw(PROGRESS_KEY_V2));
  if (v2) return v2;

  const migrated = migrateFromV1(readRaw(PROGRESS_KEY_V1), levels);
  if (!migrated) return emptyProgress();

  // Persist even an empty result, so an unparseable v1 is not re-parsed every load.
  saveProgress(migrated, levels);
  return migrated;
}

/** Persist a record. Returns false when storage rejected the write. */
export function saveProgress(
  data: ProgressData,
  levels: readonly LevelIdentity[] = LEVEL_METADATA,
): boolean {
  const ok = writeRaw(PROGRESS_KEY_V2, JSON.stringify(data));
  // Best-effort v1 mirror — rollback insurance and migration's recovery source.
  const highest = highestCompletedLevel(data, levels);
  if (highest > 0) writeRaw(PROGRESS_KEY_V1, String(highest));
  return ok;
}

/**
 * Highest 1-based level number that is cleared in the given ordering, or 0.
 * Keys for levels that no longer exist (culled or hidden) are ignored here but
 * kept in storage, so un-hiding a level restores its checkmark.
 */
export function highestCompletedLevel(
  data: ProgressData,
  levels: readonly LevelIdentity[] = LEVEL_METADATA,
): number {
  if (data.completed.length === 0) return 0;
  const done = new Set(data.completed);
  let highest = 0;
  for (let i = 0; i < levels.length; i++) {
    const key = levelKey(levels[i]);
    if (key && done.has(key)) highest = i + 1;
  }
  return highest;
}

/** Highest cleared level number from stored progress, or 0 when there is none. */
export function getHighestCompletedLevel(
  levels: readonly LevelIdentity[] = LEVEL_METADATA,
): number {
  return highestCompletedLevel(loadProgress(levels), levels);
}

/**
 * Level number the CONTINUE button should launch: one past the furthest
 * cleared level, clamped to the ladder (v1 could offer LEVEL_COUNT + 1 after
 * the final level and fail to load it).
 */
export function getNextLevel(levels: readonly LevelIdentity[] = LEVEL_METADATA): number {
  const highest = getHighestCompletedLevel(levels);
  if (highest <= 0) return 1;
  return Math.min(highest + 1, Math.max(1, levels.length));
}

/** True when the level at this 1-based number is recorded as cleared. */
export function isLevelCompleted(
  level: number,
  levels: readonly LevelIdentity[] = LEVEL_METADATA,
  data?: ProgressData,
): boolean {
  const key = levelKey(levels[level - 1]);
  if (!key) return false;
  return (data ?? loadProgress(levels)).completed.includes(key);
}

/** Record a win. Idempotent; out-of-range level numbers are a no-op. */
export function markLevelCompleted(
  level: number,
  levels: readonly LevelIdentity[] = LEVEL_METADATA,
): ProgressData {
  const data = loadProgress(levels);
  const key = levelKey(levels[level - 1]);
  if (!key || data.completed.includes(key)) return data;
  data.completed.push(key);
  saveProgress(data, levels);
  return data;
}
